import { useCallback, useState } from "react";

import { cx } from "@/lib/utils";

/**
 * Click-time validation instead of disabled buttons.
 *
 * A submit button disabled because of empty/invalid fields is a dead end:
 * the user sees it "doesn't work" but not why. The pattern here keeps the
 * button clickable, runs the checks on click and shows guiding messages
 * ("Vyplňte název.") next to the button when something is missing.
 *
 * Buttons disabled for other reasons — an in-flight mutation, missing
 * permission, a system state the user cannot fix in place — stay disabled;
 * this helper is only for form-completeness gating.
 *
 * Usage:
 *   const guard = useSubmitGuard();
 *   const submit = () => {
 *     if (!guard.check(!name.trim() && "Vyplňte název.")) return;
 *     mutate();
 *   };
 *   ...
 *   <Button variant="primary" onClick={submit} disabled={mutate.isPending}>Vytvořit</Button>
 *   <SubmitBlockers blockers={guard.blockers} />
 */

/** `condition && "message"` — a failed rule is the message itself. */
export type BlockerRule = string | false | null | undefined;

/** Messages of the rules that failed (non-empty strings). */
export function activeBlockers(rules: BlockerRule[]): string[] {
  return rules.filter((r): r is string => typeof r === "string" && r !== "");
}

export function useSubmitGuard() {
  const [blockers, setBlockers] = useState<string[]>([]);

  /** Re-evaluate rules; returns true when the submit may proceed. */
  const check = useCallback((...rules: BlockerRule[]): boolean => {
    const failed = activeBlockers(rules);
    setBlockers(failed);
    return failed.length === 0;
  }, []);

  const clear = useCallback(() => setBlockers([]), []);

  return { blockers, check, clear };
}

export function SubmitBlockers({
  blockers,
  className,
}: {
  blockers: string[];
  className?: string;
}) {
  if (blockers.length === 0) return null;
  return (
    <div
      role="alert"
      data-testid="submit-blockers"
      className={cx("space-y-0.5 text-xs text-danger", className)}
    >
      {blockers.map((message) => (
        <p key={message}>{message}</p>
      ))}
    </div>
  );
}
